import FinanceSummary from '../helpers/finance-summary';
import StringUtils from '../../utils/string-utils';
import CurrencyFormatter from '../../utils/currency-formatter-lib';
import { getNumberWithDecimals } from '../../utils/number-utils';
import Tooltip from '../../utils/tooltip';
import AnalyticsHelper from '../../utils/analytics-helper';
import { debounceAsync } from '../../utils/debounce-async';
import updateSingleSelectAria from '../helpers/single-select-aria';

export default class FinanceCalculator {
    constructor(parent) {
        this.parent = parent;
        this.el = parent.el;
        this.navigation = parent.navigation;
        this.client = parent.client;
        this.data = parent.data;
        this.car = parent.car;
        this.finance = parent.finance;
        this.pricing = parent.pricing;
        this.partEx = parent.partEx;
        this.financeLinks = this.el.querySelectorAll('a[href="#finance-calculator"]');

        this.modelCode = this.data.model.modelBodyCode;

        this.container = this.el.querySelector('#payment-options');
        if (this.container) {

            this.initElements();

            this.typeOptions = [
                ...this.container.querySelectorAll('.finance-type'),
            ];

            this.navOptions = this.el.querySelector(
                '.nav-car-financing-options',
            );
            this.navCarPrice = this.el.querySelector('.nav-car-price');

            this.financeTermsLink = this.el.querySelector(
                '.finance-terms-link',
            );
            this.financeTermsContainer = this.el.querySelector(
                '.finance-terms-container',
            );
            this.termsContainer = this.el.querySelector('#terms');

            this.templates = {
                financeResults: _.template(
                    this.container
                        .querySelector(
                            '#configurator--finance-results-template',
                        )
                        .innerHTML.replaceAll('\\', ''),
                ),
            };
            this.priceFormatter = new CurrencyFormatter({
                decimals: HONDA.currency.roundToDecimalPlaceFinance ?? 0,
            });
            this.aprFormatter = (val) => `${val.toFixed(
                +HONDA.currency.roundToDecimalPlaceFinanceAPR || 1,
            )}%`;
            this.bind();

            Tooltip.init();
        }

        this.financeLinks.forEach((link) => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.financeEl = this.el.querySelector('.configurator--finance-calculator');
                this.headerHeight = this.el.querySelector('.configurator--nav').offsetHeight;
                this.offset = this.financeEl.getBoundingClientRect().top + window.scrollY - this.headerHeight;
                window.scrollTo({
                    top: this.offset,
                    behavior: 'smooth',
                });
            });
        });
    }

    initElements(calculator = this) {
        const container = calculator.container;
        if(container) {
            calculator.calc = container.querySelector('.configurator--finance-calculator');
            calculator.loader = container.querySelector('.finance-loader');
            calculator.depositSlider = container.querySelector('.finance-deposit-slider');
            calculator.depositInput = container.querySelector('.finance-deposit-input');
            calculator.depositInputLabel = container.querySelector('.finance-deposit-label');
            calculator.hondaDepositLabel = container.querySelector('.finance-honda-deposit-label');
            calculator.totalDepositLabel = container.querySelector('.finance-total-deposit-label');
            calculator.depositMin = container.querySelector('.finance-deposit-min');
            calculator.depositMax = container.querySelector('.finance-deposit-max');
            calculator.depositMaxPCPTooltip = container.querySelector('#finance-deposit-max-pcp-tooltip');
            calculator.depositMaxHPTooltip = container.querySelector('#finance-deposit-max-hp-tooltip');
            calculator.termOptions = container.querySelector('.finance-term-options-content');
            calculator.termSlider = container.querySelector('.finance-term-slider');
            calculator.termValue = container.querySelector('.finance-term-value');
            calculator.termMin = container.querySelector('.finance-term-min');
            calculator.termMax = container.querySelector('.finance-term-max');
            calculator.mileageContainer = container.querySelector('.finance-mileage-options-container');
            calculator.mileageSlider = container.querySelector('.finance-mileage-slider');
            calculator.mileageValue = container.querySelector('.finance-mileage-value');
            calculator.mileageExcessCharge = container.querySelector('.finance-mileage-excess-charge');
            calculator.mileageExcessChargeTemplate = calculator.mileageExcessCharge.textContent;
            calculator.mileageMin = container.querySelector('.finance-mileage-min');
            calculator.mileageMax = container.querySelector('.finance-mileage-max');
            calculator.financeResults = container.querySelector('.finance-results-content');
            calculator.partExMessageContainer = container.querySelector('.finace-part-ex-message');
            calculator.partExMessage = '';
            if (calculator.partExMessageContainer) {
                calculator.partExMessagePositve = calculator.partExMessageContainer.dataset.partExPositive;
                calculator.partExMessageNegative = calculator.partExMessageContainer.dataset.partExNegative;
                calculator.partExMessageExcess = calculator.partExMessageContainer.dataset.partExExcess;
            }
        }
    }

    bind() {
        this.bindInputs();

        this?.navOptions.addEventListener('click', (e) => {
            e.preventDefault();
            this.navigation.scrollToSection('#payment-options');
        });

        this?.financeTermsLink.addEventListener('click', (e) => {
            e.preventDefault();
            this.navigation.scrollToSection('#terms');
        });

        this?.navigation.addEventListener((sectionId) => {
            if(sectionId === 'payment-options' && !this.observed) {
                this.observed = true;
                this.analyticsPush();
            }
        });
    }

    bindInputs(finance = this.finance, calculator = this) {
        calculator.typeOptions?.forEach((to) => to.addEventListener('click', (e) => this.clickTypeOption(e)));
        calculator.depositSlider?.addEventListener('input', (e) => this.inputDepositSlider(calculator));
        calculator.depositSlider?.addEventListener('change', (e) => this.changeDepositSlider(finance, calculator));
        calculator.depositInput?.addEventListener('focus', (e) => this.focusDepositInput(finance, calculator));
        calculator.depositInput?.addEventListener('blur', (e) => this.blurDepositInput(finance, calculator));
        calculator.depositInput?.addEventListener('keyup', (e) => (e.key === 'Enter' ? this.blurDepositInput(finance, calculator) : null));
        calculator.termSlider?.addEventListener('input', (e) => this.updateTermLabel(calculator));
        calculator.termSlider?.addEventListener('change', (e) => this.changeTermSlider(finance, calculator));
        calculator.mileageSlider?.addEventListener('input', (e) => this.updateMileageLabel(calculator));
        calculator.mileageSlider?.addEventListener('change', (e) => this.changeMileageSlider(finance, calculator));
    }

    async update() {
        if (this.container) {
            this.showLoader();

            const hasPricing = !!this.pricing.carMTOCEPricing;

            if (hasPricing) {
                await this.finance.getFinanceParams();
                this.finance.getCurrentParams();
            } else {
                this.finance.params = null;
            }

            const hasFinanceParams = !!this.finance.params?.products?.length;

            if (!hasPricing || !hasFinanceParams) {
                this.calc.classList.add('hide');
                this.setType('cash');
                this.updateTypeOptions();
                this.parent.financeCompareModal.update();
                this.hideLoader();
                return;
            }

            this.calc.classList.remove('hide');
            this.updateTypeOptions();

            if(this.finance.type && this.finance.type !== 'cash') {
                await this.calculateFinance();
            }

            this.parent.financeCompareModal.update();
            this.hideLoader();
        }
    }

    updateTypeOptions() {
        const types = this.finance.params?.products?.map((o) => o.financeTypeCode) ?? [];
        this.typeOptions.forEach((t) => (t.dataset.id === 'cash' || types.includes(t.dataset.id)
            ? t.classList.remove('hide')
            : t.classList.add('hide')));
        types.includes(this.finance.type)
            ? this.setType(this.finance.type)
            : this.setType('cash');

        this.updateTypeOptionAria();
    }

    clickTypeOption(e) {
        e.preventDefault();
        const el = e.currentTarget;
        this.setType(el.dataset.id);

        this.updateTypeOptionAria(el);
        this.analyticsPush();
        AnalyticsHelper.push(el);
    }

    updateTypeOptionAria(activeBtn) {
        updateSingleSelectAria(
            this.typeOptions,
            activeBtn,
            null,
            false,
        );
    }

    setType(val) {
        this.finance.type = val;
        this.typeOptions.forEach((to) => (to.dataset.id === val
            ? to.classList.add('active')
            : to.classList.remove('active')));
        if (val === 'cash') {
            this.calc.classList.add('hide');
            this.finance.deposit = null;
            this.finance.term = null;
            this.finance.mileage = null;
            this.finance.result = null;
            this.finance.financeRequest = null;
            this.finance.toUrl();
            this.parent.summary.update();
            // this.financeTermsAndConditions(); /* Dynamic T&C for finance prducts */
        } else {
            this.calc.classList.remove('hide');
            this.finance.getCurrentParams();
            this.updateInputs();
            this.calculateFinance();
        }

        this.toggleFinanceToolTips();
    }

    updateInputs(finance = this.finance, calculator = this) {
        this.updateDeposit(finance, calculator);
        this.updateTermSlider(finance, calculator);
        this.updateMileageSlider(finance, calculator);
    }

    analyticsPush() {
        const documentCountryCode = document
            .querySelector('html')
            .getAttribute('country');
        const model = this.data?.model?.modelBodyCode;
        const totalPrice = this.pricing?.getCarTotalPrice();
        const mtoce = this.car?.mtoce();
        const selectedGrade = this.car?.grade;
        const selectedColour = this.car?.colour;
        const financeTypeName = this.finance.type.toLowerCase() === 'cash' ? 'Cash' : this.finance.type.toLowerCase() === 'contract_purchase' ? 'PCP' : 'HP';
        AnalyticsHelper.push({ ecommerce: null }); // Clear the previous ecommerce object.
        AnalyticsHelper.push({
            event: 'add_payment_info',
            actionType: 'ecommerce - add payment info',
            businessUnit: 'cars',
            moduleName: 'ecommerce',
            clickText: this.finance.type,
            clickType: 'internal',
            destinationURL: '',
            modelSelected: model,
            inPageLocation: '',
            countryCode: documentCountryCode,
            ecommerce: {
                currency: window.HONDA?.currency?.ISO,
                value: totalPrice,
                payment_type: financeTypeName,
                items: [
                    {
                        item_id: mtoce,
                        item_name: model,
                        discount: 0,
                        index: 0,
                        item_brand: 'HONDA',
                        gradeName: selectedGrade,
                        colourName: selectedColour,
                        dealerName: '',
                        price: totalPrice,
                        quantity: 1,
                    },
                ],
            },
        });
    }

    toggleFinanceToolTips() {
        const type = this.finance.type;

        if(type && type.toUpperCase() === 'CONTRACT_PURCHASE') {
            this.depositMaxPCPTooltip?.classList?.remove('hide');
            this.depositMaxHPTooltip?.classList?.add('hide');
        } else if(type && type.toUpperCase() === 'HIRE_PURCHASE') {
            this.depositMaxPCPTooltip?.classList?.add('hide');
            this.depositMaxHPTooltip?.classList?.remove('hide');
        } else {
            this.depositMaxPCPTooltip?.classList?.add('hide');
            this.depositMaxHPTooltip?.classList?.add('hide');
        }
    }

    getDepositContraints(finance = this.finance) {
        const holdingFee = this.pricing.getCarHoldingFee();
        const price = this.pricing.getCarTotalPrice({ financeableAdditionalProducts: true });
        const contribution = finance.getFinanceContribution();

        const minPercent = finance?.currentParams?.productConstraints?.deposit?.range
            ?.minimum;
        const maxPercent = finance?.currentParams?.productConstraints?.deposit?.range
            ?.maximum;
        const step = finance?.currentParams?.productConstraints?.deposit?.range
            ?.interval;

        const minValue = Number((price / 100) * minPercent).toFixed(2) - contribution;
        const maxValue = Number((price / 100) * maxPercent).toFixed(2) - contribution;

        const min = Math.max(minValue, holdingFee);
        const max = maxValue;

        const defaultDepositPercent = finance?.currentParams?.productConstraints?.deposit
            ?.defaultValue;
        const defaultDepositValue = Number((price / 100) * defaultDepositPercent).toFixed(2);
        const defaultDeposit = Math.min(Math.max(defaultDepositValue, min), max)

        return {
            min,
            max,
            step,
            defaultDeposit,
        };
    }

    updateDeposit(finance = this.finance, calculator = this) {
        const {
            min, max, step, defaultDeposit,
        } = this.getDepositContraints(finance);

        calculator.depositSlider.min = min;
        calculator.depositSlider.max = max;
        calculator.depositSlider.step = step;
        calculator.depositMin.textContent = this.priceFormatter.to(+min);
        calculator.depositMax.textContent = this.priceFormatter.to(+max);
        const contribution = finance.getFinanceContribution();
        calculator.hondaDepositLabel.innerHTML = this.priceFormatter.to(contribution);
        calculator.totalDepositLabel.innerHTML = this.priceFormatter.to(Number(finance.deposit) + contribution);

        if (this.partEx.result) {
            const equity = Number(this.partEx.result.valuationAmount) - Number(this.partEx.result.outstandingFinanceAmount);

            if (equity > 0) {
                if (equity > max) {
                    calculator.partExMessage = StringUtils.replace(this.partExMessageExcess, this.priceFormatter.to(equity), this.priceFormatter.to(max));
                } else {
                    calculator.partExMessage = StringUtils.replace(this.partExMessagePositve, this.priceFormatter.to(equity));
                }
            } else if (equity < 0) {
                calculator.partExMessage = StringUtils.replace(this.partExMessageNegative, this.priceFormatter.to(equity));
            } else {
                calculator.partExMessage = '';
                calculator.partExMessageContainer.classList.add('hide');
            }

            if (calculator.partExMessage) {
                calculator.partExMessageContainer.classList.remove('hide');
                calculator.partExMessageContainer.innerHTML = calculator.partExMessage;
            }
        }

        if (
            finance.deposit === ''
            || finance.deposit === undefined
            || finance.deposit === null
            || isNaN(finance.deposit)
            || Number(finance.deposit) > max
            || finance.deposit < min
        ) {
            this.setDeposit(defaultDeposit, finance, calculator);
        } else {
            this.setDeposit(finance.deposit, finance, calculator);
        }
    }

    blurDepositInput(finance = this.finance, calculator = this) {
        const { min, max, defaultDeposit } = this.getDepositContraints(finance);
        const val = isNaN(val)
            ? this.priceFormatter.from(calculator.depositInput.value)
            : calculator.depositInput.value;

        if (Number(val) === Number(finance.deposit)) {
            calculator.depositInput.value = this.priceFormatter.to(+val);
        } else if (
            val === ''
            || val === undefined
            || val === null
            || isNaN(val)
        ) {
            this.setDeposit(defaultDeposit, finance, calculator);
        } else if (Number(val) > max) {
            this.setDeposit(max, finance, calculator);
        } else if (Number(val) < min) {
            this.setDeposit(min, finance, calculator);
        } else {
            this.setDeposit(Number(val).toFixed(2), finance, calculator);
        }

        // analytics push
        AnalyticsHelper.push({
            'event': 'configurator_payment_options_finance_cal',
            'eventCategory': 'Car Configurator',
            'eventAction': 'your contribution',
            'eventLabel': `cars|${this.modelCode}|configurator|Step 6. Payment Options|your contribution|internal|middle|#`,
            'actionType': 'your contribution',
            'businessUnit' : 'cars',
            'moduleName': 'configurator',
            'clickText': 'your contribution',
            'clickType': 'internal',
            'destinationURL': '#',
            'configuratorStepName':'Step 6. Payment Options',
            'modelSelected': `${this.modelCode}`,
            'inPageLocation': 'middle'
        });

        this.analyticsPush();
    }

    inputDepositSlider(calculator = this) {
        calculator.depositInput.value = this.priceFormatter.to(
            +calculator.depositSlider.value,
        );
    }

    focusDepositInput(finance = this.finance, calculator = this) {
        calculator.depositInput.value = finance.deposit;
        calculator.depositInput.select();
    }

    changeDepositSlider(finance = this.finance, calculator = this) {
        this.setDeposit(calculator.depositSlider.value, finance, calculator);
    }

    setDeposit(val, finance = this.finance, calculator = this) {
        finance.deposit = val;
        calculator.depositSlider.value = val;
        calculator.depositInput.value = this.priceFormatter.to(Number(val));
        calculator.depositInputLabel.innerHTML = this.priceFormatter.to(Number(val));
        calculator.totalDepositLabel.innerHTML = this.priceFormatter.to(Number(finance.deposit) + finance.getFinanceContribution());
        calculator.calculateFinance?.();
    }

    updateTermSlider(finance = this.finance, calculator = this) {
        const min = finance.currentParams?.productConstraints?.term?.range
            ?.minimum;
        const max = finance.currentParams?.productConstraints?.term?.range
            ?.maximum;
        const step = finance.currentParams?.productConstraints?.term?.range
            ?.interval;

        calculator.termSlider.min = min;
        calculator.termSlider.max = max;
        calculator.termSlider.step = step;
        calculator.termMin.textContent = min;
        calculator.termMax.textContent = max;

        if (
            finance.term === ''
            || finance.term === undefined
            || finance.term === null
            || isNaN(finance.term)
            || Number(finance.term) > Number(max)
            || Number(finance.term) < Number(min)
        ) {
            const defaultTerm = finance.currentParams?.productConstraints?.term
                ?.defaultValue;
            this.setTerm(defaultTerm, finance, calculator);
        } else {
            this.setTerm(finance.term, finance, calculator);
        }
    }

    changeTermSlider(finance = this.finance, calculator = this) {
        this.setTerm(calculator.termSlider.value, finance, calculator);
        this.analyticsPush();
    }

    setTerm(val, finance = this.finance, calculator = this) {
        finance.term = val;
        calculator.termSlider.value = val;
        this.updateTermLabel(calculator);
        calculator.calculateFinance?.();

        AnalyticsHelper.push({
            'event': 'configurator_payment_options_finance_cal',
            'eventCategory': 'Car Configurator',
            'eventAction': 'repayment term',
            'eventLabel': `cars|${this.modelCode}|configurator|Step 6. Payment Options|repayment term|internal|middle|#`,
            'actionType': 'repayment term',
            'businessUnit' : 'cars',
            'moduleName': 'configurator',
            'clickText': 'repayment term',
            'clickType': 'internal',
            'destinationURL': '#',
            'configuratorStepName':'Step 6. Payment Options',
            'modelSelected': `${this.modelCode}`,
            'inPageLocation': 'middle'
        });
    }

    updateTermLabel(calculator = this) {
        calculator.termValue.textContent = +calculator.termSlider.value;
    }

    updateMileageSlider(finance = this.finance, calculator = this) {
        const min = finance.currentParams?.productConstraints?.usage?.range
            ?.minimum;
        const max = finance.currentParams?.productConstraints?.usage?.range
            ?.maximum;
        const step = finance.currentParams?.productConstraints?.usage?.range
            ?.interval;

        if (min == null || max == null) {
            this.setMileage(null, finance, calculator);
            calculator.mileageContainer.classList.add('hide');
        } else {
            calculator.mileageSlider.min = min;
            calculator.mileageSlider.max = max;
            calculator.mileageSlider.step = step;
            calculator.mileageMin.textContent = min;
            calculator.mileageMax.textContent = max;

            if (
                finance.mileage === ''
                || finance.mileage === undefined
                || finance.mileage === null
                || isNaN(finance.mileage)
                || Number(finance.mileage) > Number(max)
                || Number(finance.mileage) < Number(min)
            ) {
                const defaultMileage = finance.currentParams?.productConstraints?.usage
                    ?.defaultValue;
                this.setMileage(defaultMileage, finance, calculator);
            } else {
                this.setMileage(finance.mileage, finance, calculator);
            }

            calculator.mileageContainer.classList.remove('hide');
        }
    }

    changeMileageSlider(finance = this.finance, calculator = this) {
        this.setMileage(calculator.mileageSlider.value, finance, calculator);
        this.analyticsPush();
    }

    setMileage(val, finance = this.finance, calculator = this) {
        finance.mileage = val;
        calculator.mileageSlider.value = val;
        this.updateMileageLabel(calculator);
        calculator.calculateFinance?.();

        AnalyticsHelper.push({
            'event': 'configurator_payment_options_finance_cal',
            'eventCategory': 'Car Configurator',
            'eventAction': 'annual mileage',
            'eventLabel': `cars|${this.modelCode}|configurator|Step 6. Payment Options|annual mileage|internal|middle|#`,
            'actionType': 'annual mileage',
            'businessUnit' : 'cars',
            'moduleName': 'configurator',
            'clickText': 'annual mileage',
            'clickType': 'internal',
            'destinationURL': '#',
            'configuratorStepName': 'Step 6. Payment Options',
            'modelSelected': `${this.modelCode}`,
            'inPageLocation': 'middle'
        });
    }

    updateMileageLabel(calculator = this) {
        calculator.mileageValue.textContent = +calculator.mileageSlider.value;
    }

    calculateFinance = debounceAsync(async () => {
        this.showLoader();
        this.parent.summary.showLoader();
        this.finance.toUrl();
        const finance = await this.finance.getFinance();
        if (finance) {
            this.financeTable();
            this.financeAttributes();
            this.financeTermsAndConditions();
            this.financeResults.classList.remove('hide');
            Tooltip.init();
        } else {
            this.financeResults.classList.add('hide');
        }
        this.parent.summary.update();
        this.hideLoader();
    }, 100);


    financeTable() {
        const financeSummary = FinanceSummary.create(this.finance);

        this.financeResults.innerHTML = this.templates.financeResults({
                finance: this.finance,
                pricing: this.pricing,
                financeSummary: financeSummary,
                stringReplace: StringUtils.replace,
                formatPrice: (val) => this.priceFormatter.to(val),
                getPercentage: ({ number, numberOfDecimals }) => `${getNumberWithDecimals({ number, numberOfDecimals })}%`,
                formatAPR: (val) => this.aprFormatter(val),
            });

            if(financeSummary?.excessMileage) {
                const excessChargeInPounds = this.priceFormatter.to((financeSummary?.excessMileage * 5000) / 100);
            this.mileageExcessCharge.textContent = StringUtils.replace(this.mileageExcessChargeTemplate, excessChargeInPounds, financeSummary?.excessMileage);
            this.mileageExcessCharge.classList.remove('hide');
            } else {
            this.mileageExcessCharge.classList.add('hide');
        }

    }

    financeAttributes() {
        this.setText(
            this.finance.result?.payments?.regularPayment?.grossValue,
            'data-finance-price',
            (val) => this.priceFormatter.to(val),
        ); // format to price
        this.setText(
            this.finance.result?.rates?.effectiveRate?.percentage,
            'data-finance-apr',
            (val) => this.aprFormatter(val),
        ); // format to apr
        this.setText(this.finance.result, 'data-finance-prop');
    }

    financeTermsAndConditions(finance) {
        this.financeTermsContainer.innerHTML = '';
        /* Dynamic T&C for finance products */
        // this.financeTermsContainer?.innerHTML = this.car.data()?.[`${this.finance.type}_TERMS`] ?? '';
    }

    getElements(attr) {
        return this.el.querySelectorAll(`[${attr}]`);
    }

    setText(data, attr, formatter) {
        const els = this.getElements(attr);
        const attrVal = (el) => el.getAttribute(`${attr}`);
        const value = (el) => (attrVal(el) ? this.get(data, attrVal(el)) : data);
        els.forEach((el) => {
            const val = value(el);
            el.textContent = formatter ? formatter(val) : val;
            el.dataset.price = val;
        });
    }

    static get(data, path) {
        return path.split('.').reduce((p, c) => (p && p[c]) || '', data);
    }

    showLoader() {
        this.loader.classList.remove('hide');
    }

    hideLoader() {
        this.loader.classList.add('hide');
    }
}
